/* OCT6 sticky lane (Oct 6 meeting) - the JS half of three flags declared in
   layout/theme.liquid (oct6:sticky markers); CSS in snippets/oct6-sticky-head.

   Every decision reads the <html> class at the moment it acts, never at
   init: an Intelligems arm or a QA param may land after this file runs. With
   the flags at their defaults nothing here touches the page - the listeners
   only look, and every visible change is gated on its class.

   1. stickyPrice   html.gd-sticky-price  CSS only. Here: the "sticky bar
                    shown" exposure for a campaign (stickyPriceTest).
   2. reviewsSticky html.gd-sproof        after a Show more tap under the
                    reviews wall, while the wall is on screen, the mobile
                    sticky bar grows a proof row (thumb, rating, price).
   3. videoCta      html.gd-vcta          after 10 s of real playback in the
                    review lightbox, a slim Add to Cart bar under the video. */
(() => {
  const root = document.documentElement;
  const on = (cls) => root.classList.contains(cls);
  const flags = () => window.GD_FLAGS || {};
  const mobile = window.matchMedia('(max-width: 989.98px)');
  const track = (name, tags) => {
    try {
      if (window.GD_FLAGS && window.GD_FLAGS.track) window.GD_FLAGS.track(name, tags || {});
    } catch (e) {
      /* tracking never breaks the page */
    }
  };
  const stickyBar = () => document.querySelector('.bm-pdp [data-sticky-atc]');

  /* ---------- 1. stickyPrice: exposure when the bar first shows ----------
     The price only exists on the bar, so a visitor counts once the bar is up:
     benchmark-pdp.js toggles .show past the buy box, and PZ-B holds it up
     from load on screens 600px tall or less (benchmark-pz-b.css), which only
     the computed visibility reports. Both arms fire, so the readout can be
     filtered to visitors who could have seen the price. */
  const watchStickyShown = () => {
    const bar = stickyBar();
    if (!bar) return;
    let fired = false;
    let mo = null;
    const check = () => {
      if (fired || flags().stickyPriceTest !== true || !mobile.matches) return;
      const up = bar.classList.contains('show') || getComputedStyle(bar).visibility === 'visible';
      if (!up) return;
      fired = true;
      if (mo) mo.disconnect();
      track('gah_stickyprice_exposure', {
        gah_stickyprice_arm: on('gd-sticky-price') ? 'b_price' : 'a_bare',
      });
    };
    mo = new MutationObserver(check);
    mo.observe(bar, { attributes: true, attributeFilter: ['class'] });
    check();
    // a bar held up from load never changes class, so a campaign flag that
    // lands after this file ran is picked up by a late look instead
    setTimeout(check, 1500);
    setTimeout(check, 4000);
  };

  /* the DEAL-5 follower rewrites any stamped node; run it once on a clone so
     a priced arm never shows the control string for a frame */
  const restamp = () => {
    try {
      if (window.GD_FLAGS && window.GD_FLAGS.applyDealPrices) window.GD_FLAGS.applyDealPrices();
    } catch (e) {
      /* the clone keeps the control string, same as the buy box would */
    }
  };
  const cloneTemplate = (name) => {
    const tpl = document.querySelector('template[data-oct6-sticky="' + name + '"]');
    const node = tpl && tpl.content && tpl.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  };

  /* ---------- 2. reviewsSticky: Show more grows the sticky bar ----------
     Daniel (Oct 6): "after you click show more, if you scroll down ... the
     sticky cart should expand ... the image, the price, and the add to cart
     ... the shine effect". Armed by a Show more tap under the reviews wall;
     the row opens on the next scroll while the wall is on screen, and closes
     when the wall leaves it. The bar's own .show still decides whether the
     bar is up at all, so the footer retract is untouched. Mobile only: the
     desktop .sticky-bar already carries thumb, stars, count and price. */
  let sproofArmed = false;
  let sproofExposed = false;
  let sproofTracked = false;
  let sproofWallOn = false;
  let sproofWall = null;
  let sproofRow = null;
  let sproofRaf = 0;

  const sproofSync = () => {
    const bar = stickyBar();
    if (!bar) return;
    const want = sproofArmed && sproofWallOn && on('gd-sproof') && mobile.matches;
    if (want && !sproofRow) {
      sproofRow = cloneTemplate('sproof');
      if (!sproofRow) return;
      bar.insertBefore(sproofRow, bar.firstChild);
      restamp();
      // one layout pass with the row closed, so opening it animates
      void sproofRow.offsetHeight;
    }
    if (!sproofRow) return;
    bar.classList.toggle('is-sproof', want);
    sproofRow.setAttribute('aria-hidden', want ? 'false' : 'true');
    if (want && !sproofTracked) {
      sproofTracked = true;
      track('gah_sproof_expand', { gah_sproof_arm: 'b_expand' });
    }
  };
  const sproofOnScroll = () => {
    if (sproofRaf) return;
    sproofRaf = requestAnimationFrame(() => {
      sproofRaf = 0;
      sproofSync();
    });
  };
  const sproofWatchWall = (wall) => {
    if (sproofWall || !wall || !('IntersectionObserver' in window)) return;
    sproofWall = wall;
    const io = new IntersectionObserver(
      ([entry]) => {
        sproofWallOn = entry.isIntersecting;
        // leaving closes at once; arriving waits for the next scroll
        if (!sproofWallOn) sproofSync();
      },
      { threshold: 0 }
    );
    io.observe(wall);
    window.addEventListener('scroll', sproofOnScroll, { passive: true });
    if (mobile.addEventListener) mobile.addEventListener('change', sproofSync);
  };
  const watchShowMore = () => {
    // capture: the wall's own loadMore runs on the same click; this only looks
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target && e.target.closest ? e.target.closest('.jm-reviews-section .jm-reviews-more__btn') : null;
        if (!btn) return;
        const armOn = on('gd-sproof');
        // both arms of a campaign count here: Show more tappers are the only
        // visitors this arm can ever reach
        if (flags().reviewsStickyTest === true && !sproofExposed && mobile.matches) {
          sproofExposed = true;
          track('gah_sproof_exposure', { gah_sproof_arm: armOn ? 'b_expand' : 'a_control' });
        }
        if (!armOn) return;
        sproofArmed = true;
        sproofWatchWall(btn.closest('.jm-reviews-section'));
      },
      true
    );
  };

  /* ---------- 3. videoCta: Add to Cart under a review video ----------
     Daniel (Oct 6): "after 10 seconds of watching the video ... an extended
     sticky cart ... shouldn't cover your whole screen. Just below the
     video". Only the full-screen review lightbox gets it (the peek box's
     own Add to Cart is already on screen under the open box).

     Watch time is real playback, never time since open: the sum of small
     forward steps of the playing video's own clock, so a seek, a loop
     restart or a paused video adds nothing.
     - native <video class="jm-lightbox__video">: its timeupdate events
       (they do not bubble; a capturing listener on document sees them)
     - Bunny iframe (.jm-lightbox__frame, iframe.mediadelivery.net): the
       embed ships player.js (playerjs 0.1.1 + pjs-implementation.js, which
       emits timeupdate {seconds, duration}). On its "ready" message this
       asks for timeupdate; a frame that never answers never shows the bar.

     The Add to Cart closes the lightbox through its own close button (video
     stopped, scroll restored) and then clicks the buy-box Add to Cart, so
     the bundle sheet, the no-sheet arms and the drawer behave exactly as a
     buy-box tap. No buy-box button on the page means no bar. */
  // Paul (Oct 8): the bar pops up 2 s into the video, not 10
  const VCTA_SECONDS = 2;
  const BUNNY_ORIGIN = 'https://iframe.mediadelivery.net';
  let vctaKey = null;
  let vctaWatched = 0;
  let vctaLast = null;
  let vctaDismissed = false;
  let vctaExposed = false;
  let vctaShownKey = null;
  let vctaBar = null;
  let vctaLb = null;

  const lightbox = () => document.querySelector('.jm-lightbox');
  const buyboxAtc = () => document.querySelector('.bm-pdp [data-buybox] .bb-atc[data-bm-atc]');
  const vctaPossible = () => !!buyboxAtc() && !!document.querySelector('template[data-oct6-sticky="vcta"]');
  // listen while either arm of a campaign (or the QA param) needs the clock
  const vctaWanted = () => on('gd-vcta') || flags().videoCtaTest === true;

  const vctaHide = () => {
    const lb = lightbox();
    if (lb) lb.classList.remove('is-oct6-vcta');
  };
  const vctaWatchLightbox = (lb) => {
    if (vctaLb === lb) return;
    vctaLb = lb;
    // closing the lightbox ends this viewing: hide the bar, restart the clock
    new MutationObserver(() => {
      if (!lb.hidden) return;
      vctaHide();
      vctaKey = null;
      vctaShownKey = null;
      vctaWatched = 0;
      vctaLast = null;
    }).observe(lb, { attributes: true, attributeFilter: ['hidden'] });
  };

  const vctaAdd = () => {
    track('gah_vcta_atc', { gah_vcta_arm: 'b_cta' });
    const atc = buyboxAtc();
    const lb = lightbox();
    const close = lb ? lb.querySelector('.jm-lightbox__close') : null;
    if (close) close.click();
    if (atc) requestAnimationFrame(() => atc.click());
  };
  const vctaDismiss = () => {
    vctaDismissed = true;
    vctaHide();
    track('gah_vcta_close', { gah_vcta_arm: 'b_cta' });
  };
  const vctaShow = () => {
    const lb = lightbox();
    if (!lb || lb.hidden || lb.classList.contains('is-oct6-vcta')) return;
    if (!vctaBar) {
      vctaBar = cloneTemplate('vcta');
      if (!vctaBar) return;
      const atcBtn = vctaBar.querySelector('[data-oct6-vcta-atc]');
      const xBtn = vctaBar.querySelector('[data-oct6-vcta-x]');
      if (atcBtn) atcBtn.addEventListener('click', vctaAdd);
      if (xBtn) xBtn.addEventListener('click', vctaDismiss);
    }
    if (vctaBar.parentNode !== lb) {
      lb.appendChild(vctaBar);
      restamp();
      // one layout pass in the closed state, so the slide-up animates
      void vctaBar.offsetHeight;
    }
    lb.classList.add('is-oct6-vcta');
    if (vctaShownKey !== vctaKey) {
      vctaShownKey = vctaKey;
      track('gah_vcta_shown', { gah_vcta_arm: 'b_cta' });
    }
  };
  const vctaReached = () => {
    if (!vctaPossible()) return;
    const armOn = on('gd-vcta');
    // both arms count at the moment the bar would show
    if (flags().videoCtaTest === true && !vctaExposed) {
      vctaExposed = true;
      track('gah_vcta_exposure', { gah_vcta_arm: armOn ? 'b_cta' : 'a_control' });
    }
    if (!armOn || vctaDismissed) return;
    vctaShow();
  };
  const vctaTick = (key, seconds) => {
    if (typeof seconds !== 'number' || !isFinite(seconds)) return;
    const lb = lightbox();
    if (!lb || lb.hidden) return;
    vctaWatchLightbox(lb);
    if (key !== vctaKey) {
      vctaKey = key;
      vctaWatched = 0;
      vctaLast = null;
    }
    if (vctaLast !== null) {
      const step = seconds - vctaLast;
      // timeupdate fires every 15-250 ms while playing: anything outside
      // that window is a seek, a loop restart or a stall, not watching
      if (step > 0 && step < 1.5) vctaWatched += step;
    }
    vctaLast = seconds;
    if (vctaWatched >= VCTA_SECONDS) vctaReached();
  };

  const watchLightboxVideo = () => {
    document.addEventListener(
      'timeupdate',
      (e) => {
        const video = e.target;
        if (!video || !video.classList || !video.classList.contains('jm-lightbox__video')) return;
        if (!vctaWanted()) return;
        vctaTick('v:' + (video.currentSrc || video.src), video.currentTime);
      },
      true
    );
    window.addEventListener('message', (e) => {
      if (e.origin !== BUNNY_ORIGIN) return;
      const lb = lightbox();
      const frame = lb ? lb.querySelector('.jm-lightbox__frame') : null;
      if (!frame || !frame.contentWindow || e.source !== frame.contentWindow) return;
      let msg = e.data;
      if (typeof msg === 'string') {
        try {
          msg = JSON.parse(msg);
        } catch (err) {
          return;
        }
      }
      if (!msg || msg.context !== 'player.js') return;
      if (msg.event === 'ready') {
        if (!vctaWanted()) return;
        try {
          frame.contentWindow.postMessage(
            JSON.stringify({
              context: 'player.js',
              version: msg.version || '0.0.11',
              method: 'addEventListener',
              value: 'timeupdate',
              listener: 'oct6-vcta',
            }),
            BUNNY_ORIGIN
          );
        } catch (err) {
          /* no handshake, no clock, no bar */
        }
        return;
      }
      if (msg.event === 'timeupdate' && msg.value) vctaTick('f:' + frame.src, Number(msg.value.seconds));
    });
  };

  const boot = () => {
    watchStickyShown();
    watchShowMore();
    watchLightboxVideo();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
